<template>
    <div class="cf-page">
        <!-- Header -->
        <div class="cf-header">
            <button class="cf-back" @click="$router.push('/cost-requests')">
                <i class="fas fa-arrow-left"></i>
            </button>
            <div>
                <h1 class="cf-title">{{ isEdit ? "Edit Request" : "New Cost Request" }}</h1>
                <p class="cf-subtitle">{{ isEdit ? `Request #${form.requestNumber || form.id}` : "Submit a new cost
                    approval request" }}</p>
            </div>
        </div>

        <div class="cf-layout">
            <!-- Left: Form -->
            <div class="cf-form-panel">
                <!-- Amount & Category -->
                <div class="cf-card">
                    <div class="cf-card-header">
                        <i class="fas fa-indian-rupee-sign"></i> Amount & Category
                    </div>
                    <div class="cf-fields">
                        <div class="cf-field">
                            <label class="cf-label">Amount <span class="cf-req">*</span></label>
                            <div class="cf-amount-wrap">
                                <span class="cf-rupee-prefix">₹</span>
                                <input class="cf-input cf-input-amount" type="number" v-model.number="form.amount"
                                    placeholder="0" min="0" />
                            </div>
                        </div>
                        <div class="cf-field">
                            <label class="cf-label">Category <span class="cf-req">*</span></label>
                            <select class="cf-select" v-model="form.categoryId">
                                <option value="">Select category</option>
                                <option v-for="c of masters.categories" :key="c.id" :value="c.id">{{ c.name }}</option>
                            </select>
                        </div>
                        <div class="cf-field">
                            <label class="cf-label">Priority <span class="cf-req">*</span></label>
                            <div class="cf-radio-group">
                                <label v-for="p of masters.priorities" :key="p.id" class="cf-radio-pill"
                                    :class="{ 'cf-radio-active': form.priorityId === p.id }"
                                    :style="form.priorityId === p.id ? { borderColor: p.color, background: p.color + '18', color: p.color } : {}">
                                    <input type="radio" :value="p.id" v-model="form.priorityId" />
                                    {{ p.name }}
                                </label>
                            </div>
                        </div>
                        <div class="cf-field">
                            <label class="cf-label">Request Type</label>
                            <div class="cf-radio-group">
                                <label class="cf-radio-pill"
                                    :class="{ 'cf-radio-active': form.requestType === 'pre_approval' }">
                                    <input type="radio" value="pre_approval" v-model="form.requestType" />
                                    <i class="fas fa-clipboard-check"></i> Pre-Approval
                                </label>
                                <label class="cf-radio-pill"
                                    :class="{ 'cf-radio-active': form.requestType === 'reimbursement' }"
                                    v-if="masters.settings.allowReimbursement !== false">
                                    <input type="radio" value="reimbursement" v-model="form.requestType" />
                                    <i class="fas fa-receipt"></i> Reimbursement
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Details -->
                <div class="cf-card">
                    <div class="cf-card-header">
                        <i class="fas fa-align-left"></i> Details
                    </div>
                    <div class="cf-fields">
                        <div class="cf-field cf-field-full">
                            <label class="cf-label">Title <span class="cf-req">*</span></label>
                            <input class="cf-input" v-model="form.title"
                                placeholder="Brief description of the expense" />
                        </div>
                        <div class="cf-field cf-field-full">
                            <label class="cf-label">Description</label>
                            <textarea class="cf-textarea" v-model="form.description" rows="4"
                                placeholder="Provide details about why this expense is needed..."></textarea>
                        </div>
                        <div class="cf-field">
                            <label class="cf-label">Bill Due Date</label>
                            <input class="cf-input" type="date" v-model="form.billDueDate" />
                        </div>
                        <div class="cf-field cf-field-full">
                            <label class="cf-label">Notes</label>
                            <textarea class="cf-textarea" v-model="form.notes" rows="2"
                                placeholder="Additional notes (optional)..."></textarea>
                        </div>
                    </div>
                </div>

                <!-- Attachments -->
                <div class="cf-card">
                    <div class="cf-card-header">
                        <i class="fas fa-paperclip"></i> Attachments
                    </div>
                    <div class="cf-fields">
                        <div class="cf-field cf-field-full">
                            <div class="cf-upload-zone" @click="$refs.fileInput.click()" @dragover.prevent
                                @drop.prevent="handleFileDrop">
                                <i class="fas fa-cloud-upload-alt cf-upload-icon"></i>
                                <span class="cf-upload-text">Drop files here or <span
                                        class="cf-upload-link">browse</span></span>
                                <span class="cf-upload-hint">Supports {{ masters.settings.allowedFileTypes || 'pdf, jpg,
                                png, doc' }} up to {{ masters.settings.maxAttachmentSizeMb || 10 }}MB (max 5
                                    files)</span>
                            </div>
                            <input type="file" ref="fileInput" multiple style="display:none" @change="handleFileSelect"
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" />
                        </div>
                        <div class="cf-field cf-field-full" v-if="form.attachments.length">
                            <div class="cf-file-list">
                                <div class="cf-file-item" v-for="(f, idx) of form.attachments" :key="idx">
                                    <i class="fas fa-file cf-file-icon"></i>
                                    <span class="cf-file-name">{{ f.name }}</span>
                                    <span class="cf-file-size">{{ formatSize(f.size) }}</span>
                                    <button class="cf-file-remove" @click="removeFile(idx)">
                                        <i class="fas fa-times"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Action Bar -->
                <div class="cf-actions">
                    <button class="cf-btn cf-btn-secondary" @click="$router.push('/cost-requests')">Cancel</button>
                    <button class="cf-btn cf-btn-outline" @click="saveDraft" :disabled="saving">
                        <i class="fas fa-save"></i> Save Draft
                    </button>
                    <button class="cf-btn cf-btn-primary" @click="submitRequest" :disabled="saving || !isValid">
                        <i class="fas fa-paper-plane" v-if="!saving"></i>
                        <i class="fas fa-circle-notch fa-spin" v-else></i>
                        {{ saving ? "Submitting..." : "Submit for Approval" }}
                    </button>
                </div>
            </div>

            <!-- Right: Sidebar preview -->
            <div class="cf-sidebar">
                <!-- Tier Info -->
                <div class="cf-side-card">
                    <div class="cf-side-title">Approval Tier</div>
                    <div class="cf-tier-display" v-if="computedTier">
                        <div class="cf-tier-badge">{{ computedTier.name }}</div>
                        <div class="cf-tier-info">
                            <div class="cf-tier-range">₹{{ formatAmount(computedTier.minAmount) }} – {{
                                computedTier.maxAmount ? '₹' + formatAmount(computedTier.maxAmount) : 'Unlimited' }}
                            </div>
                            <div class="cf-tier-approver">
                                <i class="fas fa-user-check"></i> {{ computedTier.label }}
                            </div>
                        </div>
                    </div>
                    <div class="cf-tier-empty" v-else>
                        Enter an amount to see the approval tier
                    </div>
                </div>

                <!-- All Tiers Reference -->
                <div class="cf-side-card" v-if="masters.tiers.length">
                    <div class="cf-side-title">Tier Reference</div>
                    <div class="cf-tier-ref">
                        <div class="cf-tier-row" v-for="t of sortedTiers" :key="t.id"
                            :class="{ 'cf-tier-row-active': computedTier && computedTier.id === t.id }">
                            <span class="cf-tier-row-badge">{{ t.name }}</span>
                            <span class="cf-tier-row-range">₹{{ formatAmount(t.minAmount) }} – {{ t.maxAmount ? '₹' +
                                formatAmount(t.maxAmount) : '∞' }}</span>
                            <span class="cf-tier-row-approver">{{ t.label }}</span>
                        </div>
                    </div>
                </div>

                <!-- Preview Card -->
                <div class="cf-side-card" v-if="form.title || form.amount">
                    <div class="cf-side-title">Preview</div>
                    <div class="cf-preview">
                        <div class="cf-preview-cat" v-if="selectedCategory">{{ selectedCategory.name }}</div>
                        <div class="cf-preview-title">{{ form.title || "Untitled Request" }}</div>
                        <div class="cf-preview-amount">₹{{ formattedAmount }}</div>
                        <div class="cf-preview-type" v-if="form.requestType">
                            <i
                                :class="form.requestType === 'pre_approval' ? 'fas fa-clipboard-check' : 'fas fa-receipt'"></i>
                            {{ form.requestType === "pre_approval" ? "Pre-Approval" : "Reimbursement" }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { http } from "@/utils/http"

const API_BASE = "/cost-approval-requests"

export default {
    data() {
        return {
            masters: { categories: [], priorities: [], tiers: [], settings: {} },
            form: {
                id: null,
                requestNumber: "",
                title: "",
                amount: null,
                categoryId: "",
                priorityId: "",
                requestType: "pre_approval",
                description: "",
                billDueDate: "",
                notes: "",
                attachments: [],
            },
            saving: false,
        }
    },
    computed: {
        isEdit() {
            return !!this.$route.params.id
        },
        selectedCategory() {
            if (!this.form.categoryId) return null
            return this.masters.categories.find((c) => c.id == this.form.categoryId)
        },
        computedTier() {
            if (!this.form.amount || this.form.amount <= 0) return null
            return this.sortedTiers.find((t) => {
                const min = Number(t.minAmount) || 0
                const max = t.maxAmount ? Number(t.maxAmount) : Infinity
                return this.form.amount >= min && this.form.amount <= max
            })
        },
        sortedTiers() {
            return [...this.masters.tiers].filter((t) => t.isActive !== false).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        },
        isValid() {
            return this.form.title && this.form.amount > 0 && this.form.categoryId && this.form.priorityId
        },
        formattedAmount() {
            if (!this.form.amount) return "0"
            return Number(this.form.amount).toLocaleString("en-IN")
        },
    },
    async mounted() {
        await this.fetchMasters()
        if (this.isEdit) {
            await this.fetchRequest()
        }
    },
    methods: {
        formatAmount(val) {
            if (!val && val !== 0) return "0"
            return Number(val).toLocaleString("en-IN")
        },
        formatSize(bytes) {
            if (bytes < 1024) return bytes + " B"
            if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB"
            return (bytes / 1048576).toFixed(1) + " MB"
        },
        handleFileSelect(e) {
            const files = Array.from(e.target.files || [])
            const total = this.form.attachments.length + files.length
            if (total > 5) {
                this.$toast?.error?.("Maximum 5 attachments allowed")
                return
            }
            this.form.attachments.push(...files)
            // Reset input so same file can be re-selected
            e.target.value = ""
        },
        handleFileDrop(e) {
            const files = Array.from(e.dataTransfer.files || [])
            const total = this.form.attachments.length + files.length
            if (total > 5) {
                this.$toast?.error?.("Maximum 5 attachments allowed")
                return
            }
            this.form.attachments.push(...files)
        },
        removeFile(idx) {
            this.form.attachments.splice(idx, 1)
        },
        async fetchMasters() {
            try {
                const res = await http.get(`${API_BASE}/masters`)
                const data = res.data.result || res.data || {}
                this.masters = {
                    categories: data.categories || [],
                    priorities: data.priorities || [],
                    tiers: data.tiers || [],
                    settings: data.settings || {},
                }
                // Auto-select medium priority if available
                if (!this.form.priorityId && this.masters.priorities.length) {
                    const medium = this.masters.priorities.find((p) => p.code === "medium")
                    if (medium) this.form.priorityId = medium.id
                }
            } catch (err) {
                console.error("Error fetching masters", err)
            }
        },
        async fetchRequest() {
            try {
                const res = await http.get(`${API_BASE}/${this.$route.params.id}`)
                const data = res.data.result || res.data
                if (data) {
                    this.form.id = data.id
                    this.form.requestNumber = data.requestNumber || ""
                    this.form.title = data.title || ""
                    this.form.amount = data.amount || null
                    this.form.categoryId = data.categoryId || (data.category?.id) || ""
                    this.form.priorityId = data.priorityId || (data.priority?.id) || ""
                    this.form.requestType = data.requestType || "pre_approval"
                    this.form.description = data.description || ""
                    this.form.billDueDate = data.billDueDate ? data.billDueDate.substring(0, 10) : ""
                    this.form.notes = data.notes || ""
                }
            } catch (err) {
                console.error("Error fetching request", err)
                this.$toast?.error?.("Failed to load request")
            }
        },
        buildFormData() {
            const fd = new FormData()
            fd.append("title", this.form.title)
            fd.append("amount", this.form.amount)
            fd.append("categoryId", this.form.categoryId)
            fd.append("priorityId", this.form.priorityId)
            fd.append("requestType", this.form.requestType)
            if (this.form.description) fd.append("description", this.form.description)
            if (this.form.billDueDate) fd.append("billDueDate", this.form.billDueDate)
            if (this.form.notes) fd.append("notes", this.form.notes)
            // Attachments
            this.form.attachments.forEach((f) => {
                if (f instanceof File) fd.append("attachments", f)
            })
            return fd
        },
        buildJsonPayload() {
            const payload = {
                title: this.form.title,
                amount: this.form.amount,
                categoryId: this.form.categoryId,
                priorityId: this.form.priorityId,
                requestType: this.form.requestType,
            }
            if (this.form.description) payload.description = this.form.description
            if (this.form.billDueDate) payload.billDueDate = this.form.billDueDate
            if (this.form.notes) payload.notes = this.form.notes
            return payload
        },
        async saveDraft() {
            this.saving = true
            try {
                if (this.isEdit) {
                    // Update draft — use JSON (no file upload here)
                    await http.patch(`${API_BASE}/${this.form.id}`, this.buildJsonPayload())
                    // Upload new attachments if any
                    await this.uploadAttachments(this.form.id)
                } else {
                    // Create with multipart/form-data
                    const res = await http.post(API_BASE, this.buildFormData(), {
                        headers: { "Content-Type": "multipart/form-data" },
                    })
                    const created = res.data.result || res.data
                    this.form.id = created.id
                }
                this.$toast?.success?.("Draft saved")
                this.$router.push("/cost-requests")
            } catch (err) {
                const msg = err.response?.data?.message || "Failed to save draft"
                this.$toast?.error?.(msg)
            }
            this.saving = false
        },
        async submitRequest() {
            if (!this.isValid) return
            this.saving = true
            try {
                if (this.isEdit) {
                    // Update draft first, then submit
                    await http.patch(`${API_BASE}/${this.form.id}`, this.buildJsonPayload())
                    await this.uploadAttachments(this.form.id)
                    await http.patch(`${API_BASE}/${this.form.id}/submit`, {})
                } else {
                    // POST auto-creates and submits for approval
                    await http.post(API_BASE, this.buildFormData(), {
                        headers: { "Content-Type": "multipart/form-data" },
                    })
                }
                this.$toast?.success?.("Request submitted for approval")
                this.$router.push("/cost-requests")
            } catch (err) {
                const msg = err.response?.data?.message || "Failed to submit request"
                this.$toast?.error?.(msg)
            }
            this.saving = false
        },
        async uploadAttachments(requestId) {
            const files = this.form.attachments.filter((f) => f instanceof File)
            if (!files.length) return
            const fd = new FormData()
            files.forEach((f) => fd.append("attachments", f))
            await http.post(`${API_BASE}/${requestId}/attachments`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            })
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.cf-page {
    padding: 20px;
}

/* Header */
.cf-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
}

.cf-back {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: 1px solid $gray200;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: $gray600;
    transition: all $transitionFast;

    &:hover {
        border-color: $primaryColor;
        color: $primaryColor;
    }
}

.cf-title {
    font-size: 20px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
}

.cf-subtitle {
    font-size: 12.5px;
    color: $gray400;
    margin: 2px 0 0;
}

/* Layout */
.cf-layout {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 20px;
    align-items: flex-start;
}

@media (max-width: 1024px) {
    .cf-layout {
        grid-template-columns: 1fr;
    }
}

/* Form Panel */
.cf-form-panel {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

/* Cards */
.cf-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusMd;
    overflow: hidden;
}

.cf-card-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    font-size: 13px;
    font-weight: 700;
    color: $gray700;
    border-bottom: 1px solid $gray100;

    i {
        color: $primaryColor;
        font-size: 13px;
    }
}

/* Fields */
.cf-fields {
    padding: 16px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
}

.cf-field-full {
    grid-column: 1 / -1;
}

.cf-label {
    display: block;
    font-size: 11.5px;
    font-weight: 600;
    color: $gray600;
    margin-bottom: 4px;
}

.cf-req {
    color: $danger;
}

.cf-input,
.cf-select,
.cf-textarea {
    width: 100%;
    padding: 9px 12px;
    border: 1px solid $gray200;
    border-radius: 8px;
    font-size: 13px;
    font-family: inherit;
    outline: none;
    transition: border $transitionFast;
    background: white;
    color: $gray800;

    &:focus {
        border-color: $primaryColor;
        box-shadow: 0 0 0 3px rgba(0, 94, 137, .08);
    }

    &::placeholder {
        color: $gray300;
    }
}

.cf-select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    padding-right: 30px;
}

.cf-textarea {
    resize: vertical;
    min-height: 80px;
}

/* Amount input */
.cf-amount-wrap {
    position: relative;
}

.cf-rupee-prefix {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 14px;
    font-weight: 600;
    color: $gray500;
}

.cf-input-amount {
    padding-left: 28px;
    font-size: 18px;
    font-weight: 700;
}

/* Radio pill group */
.cf-radio-group {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.cf-radio-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 14px;
    border: 1px solid $gray200;
    border-radius: $radiusFull;
    font-size: 12px;
    font-weight: 600;
    color: $gray600;
    cursor: pointer;
    transition: all $transitionFast;

    input {
        display: none;
    }

    i {
        font-size: 11px;
    }

    &:hover {
        border-color: $gray300;
    }
}

.cf-radio-active {
    border-color: $primaryColor;
    background: $primaryLight;
    color: $primaryColor;
}

/* Upload area */
.cf-upload-zone {
    border: 2px dashed $gray200;
    border-radius: 10px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    transition: border-color $transitionFast;

    &:hover {
        border-color: $primaryColor;
    }
}

.cf-upload-icon {
    font-size: 24px;
    color: $gray300;
    margin-bottom: 4px;
}

.cf-upload-text {
    font-size: 13px;
    color: $gray500;
}

.cf-upload-link {
    color: $primaryColor;
    font-weight: 600;
}

.cf-upload-hint {
    font-size: 11px;
    color: $gray400;
}

/* File list */
.cf-file-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.cf-file-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    background: $gray50;
    border-radius: 6px;
}

.cf-file-icon {
    color: $primaryColor;
    font-size: 13px;
}

.cf-file-name {
    flex: 1;
    font-size: 12.5px;
    color: $gray700;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.cf-file-size {
    font-size: 11px;
    color: $gray400;
    flex-shrink: 0;
}

.cf-file-remove {
    background: none;
    border: none;
    color: $gray400;
    cursor: pointer;
    font-size: 12px;
    padding: 2px 4px;

    &:hover {
        color: $danger;
    }
}

/* Actions */
.cf-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    padding-top: 4px;
}

.cf-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 9px 18px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    font-family: inherit;
    transition: all $transitionFast;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.cf-btn-primary {
    background: $primaryColor;
    color: white;

    &:hover:not(:disabled) {
        background: $primaryDark;
    }
}

.cf-btn-secondary {
    background: $gray100;
    color: $gray600;

    &:hover {
        background: $gray200;
    }
}

.cf-btn-outline {
    background: white;
    border: 1px solid $gray200;
    color: $gray700;

    &:hover {
        border-color: $primaryColor;
        color: $primaryColor;
    }
}

/* Sidebar */
.cf-sidebar {
    display: flex;
    flex-direction: column;
    gap: 14px;
    position: sticky;
    top: 20px;
}

.cf-side-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusMd;
    padding: 14px 16px;
}

.cf-side-title {
    font-size: 12px;
    font-weight: 700;
    color: $gray500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 10px;
}

/* Tier display */
.cf-tier-display {
    display: flex;
    align-items: center;
    gap: 12px;
}

.cf-tier-badge {
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 800;
    color: white;
    background: $primaryColor;
    white-space: nowrap;
}

.cf-tier-info {
    flex: 1;
}

.cf-tier-range {
    font-size: 14px;
    font-weight: 700;
    color: $gray800;
}

.cf-tier-approver {
    font-size: 12px;
    color: $gray500;
    margin-top: 2px;

    i {
        color: $primaryColor;
        margin-right: 3px;
    }
}

.cf-tier-empty {
    font-size: 12.5px;
    color: $gray400;
    padding: 8px 0;
}

/* Tier reference table */
.cf-tier-ref {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.cf-tier-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 6px;
    transition: background $transitionFast;
}

.cf-tier-row-active {
    background: $primaryLight;
}

.cf-tier-row-badge {
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 800;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: $primaryColor;
}

.cf-tier-row-range {
    flex: 1;
    font-size: 11.5px;
    color: $gray600;
    font-weight: 500;
}

.cf-tier-row-approver {
    font-size: 11px;
    color: $gray400;
    font-weight: 500;
}

.cf-tier-row-active .cf-tier-row-range {
    color: $primaryDark;
    font-weight: 600;
}

.cf-tier-row-active .cf-tier-row-approver {
    color: $primaryColor;
}

/* Preview card */
.cf-preview {
    background: $gray50;
    border-radius: 8px;
    padding: 12px;
}

.cf-preview-cat {
    font-size: 10px;
    font-weight: 600;
    color: $primaryColor;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
}

.cf-preview-title {
    font-size: 13px;
    font-weight: 600;
    color: $gray800;
    margin-bottom: 4px;
}

.cf-preview-amount {
    font-size: 20px;
    font-weight: 800;
    color: $gray900;
    margin-bottom: 6px;
}

.cf-preview-type {
    font-size: 11.5px;
    color: $gray500;
    display: flex;
    align-items: center;
    gap: 4px;

    i {
        color: $primaryColor;
    }
}
</style>
